import type { TFunction } from 'i18next';
import type {
  DashboardDailyPoint,
  GroupingRow,
  LossRow,
  PaymentTypeRow,
  ProductRow,
} from './dashboard.types';
import { StatCell } from './growth';
import type { SortableColumn } from '@/components/ui/sortable-table';

/** The Revenue tiles, one row per day of the chart. */
export function dailyColumns(t: TFunction): SortableColumn<DashboardDailyPoint>[] {
  return [
    {
      key: 'date',
      header: t('dashboard.date'),
      sortValue: (row) => row.date,
      render: (row) => (
        <div>
          <p className="whitespace-nowrap">{row.label}</p>
          <p className="text-muted-foreground text-xs whitespace-nowrap">
            {t('dashboard.vs')} {row.previousLabel}
          </p>
        </div>
      ),
    },
    {
      key: 'orders',
      header: t('dashboard.orders'),
      align: 'right',
      sortValue: (row) => row.sales.orders.value,
      render: (row) => <StatCell stat={row.sales.orders} isMoney={false} />,
    },
    {
      key: 'grossSalesCost',
      header: t('dashboard.costOfGoods'),
      align: 'right',
      sortValue: (row) => row.sales.grossSalesCost.value,
      render: (row) => <StatCell stat={row.sales.grossSalesCost} isMoney />,
    },
    {
      key: 'grossSales',
      header: t('dashboard.grossSales'),
      align: 'right',
      sortValue: (row) => row.sales.grossSales.value,
      render: (row) => <StatCell stat={row.sales.grossSales} isMoney />,
    },
    {
      key: 'netSales',
      header: t('dashboard.netSales'),
      align: 'right',
      sortValue: (row) => row.sales.netSales.value,
      render: (row) => <StatCell stat={row.sales.netSales} isMoney />,
    },
    {
      key: 'averageTicket',
      header: t('dashboard.averageTicket'),
      align: 'right',
      sortValue: (row) => row.sales.averageTicket.value,
      render: (row) => <StatCell stat={row.sales.averageTicket} isMoney />,
    },
    {
      key: 'amountCollected',
      header: t('dashboard.amountCollected'),
      align: 'right',
      sortValue: (row) => row.sales.amountCollected.value,
      render: (row) => <StatCell stat={row.sales.amountCollected} isMoney />,
    },
  ];
}

export function paymentColumns(t: TFunction): SortableColumn<PaymentTypeRow>[] {
  return [
    {
      key: 'name',
      header: t('dashboard.name'),
      sortValue: (row) => row.name,
      render: (row) => row.name,
    },
    {
      key: 'orders',
      header: t('dashboard.orders'),
      align: 'right',
      sortValue: (row) => row.orders.value,
      render: (row) => <StatCell stat={row.orders} isMoney={false} />,
    },
    {
      key: 'amountCollected',
      header: t('dashboard.amountCollected'),
      align: 'right',
      sortValue: (row) => row.amountCollected.value,
      render: (row) => <StatCell stat={row.amountCollected} isMoney />,
    },
    {
      key: 'refundTotal',
      header: t('dashboard.refundTotal'),
      align: 'right',
      sortValue: (row) => row.refundTotal.value,
      render: (row) => <StatCell stat={row.refundTotal} isMoney />,
    },
    {
      key: 'salesTotal',
      header: t('dashboard.salesTotal'),
      align: 'right',
      sortValue: (row) => row.salesTotal.value,
      render: (row) => <StatCell stat={row.salesTotal} isMoney />,
    },
    {
      key: 'averageTicket',
      header: t('dashboard.averageTicket'),
      align: 'right',
      sortValue: (row) => row.averageTicket.value,
      render: (row) => <StatCell stat={row.averageTicket} isMoney />,
    },
  ];
}

export function groupingColumns(t: TFunction): SortableColumn<GroupingRow>[] {
  return [
    {
      key: 'name',
      header: t('dashboard.name'),
      sortValue: (row) => row.name,
      render: (row) => row.name,
    },
    {
      key: 'sold',
      header: t('dashboard.sold'),
      align: 'right',
      sortValue: (row) => row.sold.value,
      render: (row) => <StatCell stat={row.sold} isMoney={false} />,
    },
    {
      key: 'orders',
      header: t('dashboard.orders'),
      align: 'right',
      sortValue: (row) => row.orders.value,
      render: (row) => <StatCell stat={row.orders} isMoney={false} />,
    },
    {
      key: 'salesTotal',
      header: t('dashboard.salesTotal'),
      align: 'right',
      sortValue: (row) => row.salesTotal.value,
      render: (row) => <StatCell stat={row.salesTotal} isMoney />,
    },
    {
      key: 'profit',
      header: t('dashboard.profit'),
      align: 'right',
      sortValue: (row) => row.profit.value,
      render: (row) => <StatCell stat={row.profit} isMoney />,
    },
  ];
}

export function productColumns(t: TFunction): SortableColumn<ProductRow>[] {
  return [
    {
      key: 'name',
      header: t('dashboard.name'),
      sortValue: (row) => row.name,
      render: (row) => row.name,
    },
    {
      key: 'upc',
      header: t('dashboard.upc'),
      sortValue: (row) => row.upc,
      render: (row) => (
        <span className="text-muted-foreground tabular-nums">{row.upc}</span>
      ),
    },
    {
      key: 'sold',
      header: t('dashboard.sold'),
      align: 'right',
      sortValue: (row) => row.sold.value,
      render: (row) => <StatCell stat={row.sold} isMoney={false} />,
    },
    {
      key: 'orders',
      header: t('dashboard.orders'),
      align: 'right',
      sortValue: (row) => row.orders.value,
      render: (row) => <StatCell stat={row.orders} isMoney={false} />,
    },
    {
      key: 'salesTotal',
      header: t('dashboard.salesTotal'),
      align: 'right',
      sortValue: (row) => row.salesTotal.value,
      render: (row) => <StatCell stat={row.salesTotal} isMoney />,
    },
    {
      key: 'profit',
      header: t('dashboard.profit'),
      align: 'right',
      sortValue: (row) => row.profit.value,
      render: (row) => <StatCell stat={row.profit} isMoney />,
    },
  ];
}

export function lossColumns(t: TFunction): SortableColumn<LossRow>[] {
  return [
    {
      key: 'reason',
      header: t('dashboard.reason'),
      sortValue: (row) => t(row.reasonKey),
      render: (row) => t(row.reasonKey),
    },
    {
      key: 'units',
      header: t('dashboard.units'),
      align: 'right',
      sortValue: (row) => row.units.value,
      render: (row) => <StatCell stat={row.units} isMoney={false} />,
    },
  ];
}
